import { Duplex, Writable, Readable, pipeline } from 'stream';
import i18n, { i18n_t } from '@/locales/i18n';
import { errorStringify, initId } from '@/renderer/scripts/utils/tools';
import LimitStream from '@/renderer/scripts/utils/StreamManager/utils/LimitStream';
import { IS_BROWSER } from '@/common/constant';

let fs: typeof import('fs');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
}

type StreamsBean = {
  id: string;
  sourceStream: Readable | Duplex;
  limitStream: LimitStream;
  destStream: Writable | Duplex | null;
};

/**
 * type=up时，path是上传文件路径
 * type=down时，path是下载文件路径，responseStream是Axios响应流
 */
type GroupOptions = { type: 'upload'; path: string } | { type: 'download'; responseStream: Duplex; path: string };

/**
 * 流管理器
 * 内部包含了多个stream
 * @description
 * 一个 streamGroup 对应一个文件 一个Uploader|Downloader
 * 所以，当前group的属性，你就可以理解为是文件一个维度。
 *
 * 1. 简单文件上传，一般只有一个链接，所以这里面的Stream数量应该也只有一个
 * 2. 大文件分块上传会有多个分块同时上传，所以这里的stream数量可能会同时存在多个
 */
export class StreamGroup {
  public readonly id = initId('streamGroup');
  // 上传、下载2个类型
  private readonly type: 'upload' | 'download';
  // 文件名
  public readonly fileName: string;
  // stream总数
  public streamCount: number;
  // 所有的流发送的数据总数
  public totalSentBytes: number;
  // 当前组内所有流的速度统计（单位kb/s)
  public totalSpeed: number;
  private _totalSentBytesMap: Map<string, number>;
  // 所有stream的集合
  private _streamsMap: Map<string, StreamsBean>;

  // 总限速度
  _limit: boolean | null;
  _limitKbs: number | null;

  constructor(type: 'upload' | 'download', fileName: string) {
    this.type = type;
    this.fileName = fileName;
    this.streamCount = 0;
    this.totalSentBytes = 0;
    this.totalSpeed = 0;
    this._streamsMap = new Map();
    this._totalSentBytesMap = new Map();
    this._limitKbs = null;
    this._limit = null;
  }

  /**
   * 创建一个需要受到限速的可读（双向）流
   * 若type === 'path' 必须传递path
   * 若type === 'response' 必须传responseStream
   * @description 当前类在创建stream需要负责流的关闭操作
   * 原则上：谁创建，谁关闭
   * 注意：如果 Readable 流在处理过程中触发错误，则 Writable 目标不会自动关闭
   * @param groupOptions
   * @param options
   * @param callback
   */
  public createLimitStream(groupOptions: GroupOptions, options?: any): StreamsBean {
    // 来源Stream
    let sourceStream: Readable | Duplex | null = null;
    // 限速转换流
    let limitStream: LimitStream | null = this._createLimitStream(groupOptions.type);
    // 目标stream
    let destStream: Writable | Duplex | null = null;

    // 若是path，创建一个读文件流
    if (groupOptions.type === 'upload') {
      // sourceStream = new MyReadableStream(groupOptions.path, { ...options, autoClose: true });
      sourceStream = fs.createReadStream(groupOptions.path, { ...options, autoClose: true });
    }
    // 如果是response，创建一个写文件流
    if (groupOptions.type === 'download') {
      sourceStream = groupOptions.responseStream;
      destStream = fs.createWriteStream(groupOptions.path, { ...options, autoClose: true });
    }
    // 目标流必须存在
    if (!sourceStream) throw new Error(i18n_t('错误类型的参数'));
    let streamBean: StreamsBean;
    const streamBeanId = initId('streamBean');
    //  type === 'download'
    if (destStream !== null) {
      sourceStream.pipe(limitStream);
      limitStream.pipe(destStream);
      // 使用pipe就必须承担起内存回收工作
      sourceStream.on('error', err => {
        limitStream = null;
        destStream?.destroy(new Error(`destroy by sourceStream error cause ${err.message}`));
      });
      limitStream.on('error', err => {
        limitStream = null;
        destStream?.destroy(new Error(`destroy by limitStream error cause ${err.message}`));
      });
      // 注意 destStream写文件流出错，需要重试
      streamBean = { id: streamBeanId, sourceStream, limitStream, destStream };
      this._setStream(streamBeanId, streamBean);
    } else {
      //  type === 'up'
      sourceStream.pipe(limitStream);
      // 使用pipe就必须承担起内存回收工作
      sourceStream.on('error', err => {
        sourceStream?.destroy();
        limitStream = null;
      });
      streamBean = { id: streamBeanId, sourceStream, limitStream, destStream: null };
      this._setStream(streamBeanId, streamBean);
    }
    this._appendStreamInfo(streamBean);
    return streamBean;
  }

  /**
   * 根据limitStreamId,销毁某一个streamBean
   * @description
   * 同时还会删除内部相关引用，修改相关streamCount
   * deleteStream 只是取用流的引用（GC会自动回收）
   * destroyStream 流对象主动进行进行destroy
   * @param streamBeanId
   */
  destroyStreamByBeanId(streamBeanId: string) {
    // logger.ui(`StreamGroup`, `destroy Stream By id${streamBeanId}`);
    if (streamBeanId === '') {
      return;
    }
    this._destroyStream(streamBeanId);
    this._deleteStream(streamBeanId);
  }
  /**
   * 销毁所有的stream管道
   * @description 在任务被暂停的时候调用，把当前的所有流都销毁掉
   */
  destroyAllStreams() {
    const streamBeanIds = this._streamsMap.keys();
    for (const streamBeanId of streamBeanIds) {
      this._destroyStream(streamBeanId);
      this._deleteStream(streamBeanId);
    }
  }

  /**
   * 根据传参来更新每一个流的limitSize
   * @description
   * @param speedLimit true为开启限速，若为false，设置limit值为0（不限速）
   * @param options
   * @private
   */
  public fixSpeedByLimit(
    speedLimit: boolean,
    options?: {
      totalStreamCount: null | number;
      fileSpeedLimitSizeKbs: null | number;
      totalSpeedLimitSizeKbs: null | number;
    },
  ) {
    const fileSpeedLimitSizeKbs = options?.fileSpeedLimitSizeKbs;
    const totalSpeedLimitSizeKbs = options?.totalSpeedLimitSizeKbs;
    const totalStreamCount = options?.totalStreamCount;
    if (speedLimit) {
      // 外部限速
      if (fileSpeedLimitSizeKbs !== null && fileSpeedLimitSizeKbs !== undefined) {
        if (this.streamCount === 0) return;
        // 文件/流数量
        const streamSizeKbs = Math.round(fileSpeedLimitSizeKbs / this.streamCount);
        this._streamsMap.forEach(({ limitStream }) => {
          this._limit = true;
          this._limitKbs = fileSpeedLimitSizeKbs;
          limitStream.setLimit(streamSizeKbs);
        });
      }
      // 内部限速
      if (
        totalSpeedLimitSizeKbs !== null &&
        totalSpeedLimitSizeKbs !== undefined &&
        totalStreamCount !== null &&
        totalStreamCount !== undefined
      ) {
        const streamSize = Math.round(totalSpeedLimitSizeKbs / totalStreamCount);
        // 计算得到有多少个文件在运行中
        const fileSize = totalStreamCount / this.streamCount;
        const fileStreamSizeKbs = Math.round(totalSpeedLimitSizeKbs / fileSize);
        this._limit = true;
        this._limitKbs = fileStreamSizeKbs;
        this._streamsMap.forEach(({ limitStream }) => {
          limitStream.setLimit(streamSize);
        });
      }
    }
    // 不限速的话，应该取消掉Limit
    else {
      this._limit = false;
      this._limitKbs = null;
      this._streamsMap.forEach(limitStream => {
        limitStream.limitStream.reSetLimit();
      });
    }
  }

  // 设置组内一个stream
  private _setStream(streamBeanId: string, streamBean: StreamsBean) {
    this._streamsMap.set(streamBeanId, streamBean);
    this._totalSentBytesMap.set(streamBeanId, 0);
    this.streamCount = this._streamsMap.size;
  }
  // 关闭对应的流
  private _destroyStream(streamBeanId: string) {
    const streamsBean = this._streamsMap.get(streamBeanId);
    if (!streamsBean) return;
    if (this.type === 'upload') {
      streamsBean.destStream?.destroy();
      streamsBean.limitStream?.destroy();
      streamsBean.sourceStream?.destroy();
    }
    if (this.type === 'download') {
      streamsBean.destStream?.destroy();
      streamsBean.limitStream?.destroy();
      streamsBean.sourceStream?.destroy();
    }
  }
  // 删除组内一个stream
  private _deleteStream(streamBeanId: string) {
    this._streamsMap.delete(streamBeanId);
    this._totalSentBytesMap.delete(streamBeanId);
    this.streamCount = this._streamsMap.size;
  }
  // 遍历组内所有的Map，统计所有流内通过的数据size
  private _calcTotalSentBytes() {
    let tmpTotalBytes = 0;
    this._totalSentBytesMap.forEach(sentBytes => {
      tmpTotalBytes += sentBytes;
    });
    this.totalSentBytes = tmpTotalBytes;
    this._calcTotalSpeed();
  }
  // 统计当前组内速度
  // TODO Loganylwu 实现统计全部速度
  private _calcTotalSpeed(): number {
    return 100;
  }
  // 叠加数据，同时还会更新 totalSentBytes 的值
  private _countSentBytesByStreamId(streamId: string, sentBytes: number) {
    const item = this._totalSentBytesMap.get(streamId);
    item !== undefined && this._totalSentBytesMap.set(streamId, sentBytes);
    this._calcTotalSentBytes();
  }
  // 直接清除对应的streamId的sentBytes,同时还会更新 totalSentBytes 的值
  private _clearSentBytesByStreamId(streamId: string) {
    const item = this._totalSentBytesMap.get(streamId);
    item !== undefined && this._totalSentBytesMap.set(streamId, 0);
    this._calcTotalSentBytes();
  }

  /**
   * 创建一个限速流
   * @private
   */
  private _createLimitStream(type: 'upload' | 'download') {
    const originLimitStream = new LimitStream();
    // 监控每一个stream的数据传输
    const setHandler = (target: LimitStream, prop: PropertyKey, value: any, receiver: any): boolean => {
      // @ts-ignore
      target[prop] = value;
      if (prop === 'sentBytes') {
        this._countSentBytesByStreamId(target.id, value as number);
      }
      return true;
    };

    // 流被关闭的时候，需要清理传输数据，释放流的引用
    const streamOnEnd = () => {
      this._clearSentBytesByStreamId(originLimitStream.id);
    };
    // 若是path,则是 readStream->limitStream->requestStream ，应当监听limitStream的结束，
    // 不监听readStream，是因为readStream耗尽时，不见得limitStream就一定耗尽
    // 若是response，则是 requestStream->limitStream-> writeStream ，应当监听writeStream的结束
    // 增加限速流的销毁处理逻辑
    if (type === 'upload') {
      originLimitStream.on('close', streamOnEnd);
      originLimitStream.on('error', streamOnEnd);
      originLimitStream.on('end', streamOnEnd);
    }
    return new Proxy(originLimitStream, {
      set: setHandler,
    });
  }

  private _appendStreamInfo(streamBean: StreamsBean) {
    const { sourceStream, limitStream, destStream } = streamBean;
    // 上传类型处理
    if (this.type === 'upload') {
      // srouceStream
      sourceStream.once('error', err => {
        err.name = 'Readable';
        // @ts-ignore
        err.streamBeanId = streamBean.id;
      });
      // destStream 上传没有destStream
    }
    // 下载类型处理
    if (this.type === 'download') {
      sourceStream.once('error', err => {
        err.name = 'Axios';
        // @ts-ignore
        err.streamBeanId = streamBean.id;
      });
      destStream !== null &&
        destStream.once('error', err => {
          err.name = 'Writable';
          // @ts-ignore
          err.streamBeanId = streamBean.id;
        });
    }
    // limitStream是公共的
    limitStream.once('error', err => {
      err.name = 'Duplex';
      // @ts-ignore
      err.streamBeanId = streamBean.id;
    });
  }
}
